import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Button } from "@/components/ui/button";
import { 
  Save, 
  Archive, 
  Clock, 
  Sparkles, 
  Plus, 
  Copy, 
  Star,
  Pin,
  Target,
  ChevronDown
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { AnalysisPanel, AnalysisPanelHandle } from "./AnalysisPanel";
import { FileAttachment } from "@/types/attachment";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CurrentFocusContainerMemento } from "@/types/memento";
import { useLocalStorageBackup } from "@/hooks/useLocalStorageBackup";
import { LastSavedIndicator } from "./LastSavedIndicator";

export interface CurrentFocusContainerHandle {
  loadMemento: (memento: CurrentFocusContainerMemento) => Promise<void>;
}

interface CurrentFocusContainerProps {
  onOpenHistory?: () => void;
}

export const CurrentFocusContainer = forwardRef<
  CurrentFocusContainerHandle,
  CurrentFocusContainerProps
>((props, ref) => {
  const { onOpenHistory } = props;
  const [analysisContent, setAnalysisContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentMementoId, setCurrentMementoId] = useState<string | null>(null);
  const [displayedMementoId, setDisplayedMementoId] = useState<string | null>(null); // What's shown (can be viewed without focus)
  const [currentFocusId, setCurrentFocusId] = useState<string | null>(null); // What's actively being worked on
  const [isRestoring, setIsRestoring] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [focusTitle, setFocusTitle] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  const analysisPanelRef = useRef<AnalysisPanelHandle>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // LocalStorage backup for instant recovery
  const { saveImmediately, clearBackup, restoreFromLocalStorage } = useLocalStorageBackup(
    () => analysisPanelRef.current?.getState() || null,
    currentMementoId,
    !isRestoring // Disable during initial restore
  );

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    loadMemento: handleLoadMemento,
  }));

  // Auto-restore on mount (Phase 1: Never Lose Work)
  useEffect(() => {
    const restoreState = async () => {
      try {
        // Step 1: Try localStorage first (instant restore)
        const localBackup = restoreFromLocalStorage();
        
        if (localBackup) {
          console.log('[CurrentFocus] Restoring from localStorage backup');
          analysisPanelRef.current?.setState(localBackup.lastState as any);
          setAnalysisContent(localBackup.lastState.currentContent);
          setCurrentMementoId(localBackup.currentMementoId);
          
          // Only show toast on actual page load, not during HMR in dev
          if (!import.meta.hot) {
            toast.success('✓ Restored your work from browser cache', {
              description: `Last saved: ${new Date(localBackup.lastSavedAt).toLocaleTimeString()}`,
            });
          }
        }

        // Step 2: Query DB for most recent memento (richer history)
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: mementos, error } = await supabase
            .from('current_focus_container_mementos')
            .select('*')
            .eq('user_id', user.id)
            .eq('deleted', false)
            .order('created_at', { ascending: false })
            .limit(1);

          if (!error && mementos && mementos.length > 0) {
            const latestMemento = mementos[0] as any as CurrentFocusContainerMemento;
            
            // Only load from DB if it's newer than localStorage
            if (!localBackup || new Date(latestMemento.created_at) > new Date(localBackup.lastSavedAt)) {
              console.log('[CurrentFocus] DB memento is newer, loading from DB');
              await handleLoadMemento(latestMemento);
            }
          }
        }
      } catch (error) {
        console.error('[CurrentFocus] Error during auto-restore:', error);
        // Don't show error toast - fallback to empty state is fine
      } finally {
        setIsRestoring(false);
      }
    };

    restoreState();
  }, []); // Only run once on mount

  // Auto-save timer - only saves if DATA CHANGES exist (not just clicking around viewing)
  useEffect(() => {
    // Auto-save fires when hasUnsavedChanges=true (which only happens on actual edits)
    // This works whether you're focused or just viewing - data change is the key
    if (hasUnsavedChanges && !isRestoring) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setTimeout(() => {
        handleSaveDraft("auto_save");
        setHasUnsavedChanges(false);
      }, 5000); // 5 seconds - aggressive auto-save for data safety
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, isRestoring]);

  // Keyboard shortcut: Ctrl+S for versioning
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveDraft("version"); // Create version
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSaveDraft = async (
    triggerType: "manual_save" | "auto_save" | "stash" | "new_focus" | "side_quest" | "version" | "viewed" | "switched_focus" | "history_load" | "iterate"
  ) => {
    setIsSaving(true);
    try {
      let {
        data: { user },
      } = await supabase.auth.getUser();

      // If no user, sign in anonymously (guest user)
      if (!user) {
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();

        if (anonError || !anonData?.user) {
          console.error("Failed to sign in anonymously:", anonError);
          if (triggerType !== "stash") {
            toast.error("Authentication failed. Please refresh the page.");
          }
          return null;
        }

        user = anonData.user;
      }

      // Get state from analysis panel
      const analysisState = analysisPanelRef.current?.getState();

      if (!analysisState) {
        console.error("Could not get analysis panel state");
        return null;
      }

      // Insert memento
      const { data, error } = await supabase
        .from("current_focus_container_mementos")
        .insert({
          user_id: user.id,
          trigger_type: triggerType,
          analysis_original_content: analysisState.originalContent || "",
          analysis_current_content: analysisState.currentContent || "",
          analysis_command_text: analysisState.commandText || "",
          analysis_changes: analysisState.changes as any,
          analysis_comments: analysisState.comments as any,
          analysis_attachments: analysisState.attachments as any,
          synthesis_content: "",
          synthesis_iteration_instructions: "",
          synthesis_attachments: [] as any,
          custom_title: focusTitle || null,
          pinned: isPinned,
          favorited: isFavorited,
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Clear localStorage backup after successful DB save
      clearBackup();

      // Only show toast for manual saves (auto-save is shown in LastSavedIndicator)
      if (triggerType === "manual_save" || triggerType === "version") {
        toast.success(triggerType === "version" ? "Version created" : "Draft saved");
      } else if (triggerType === "new_focus") {
        toast.success("New focus started");
      } else if (triggerType === "side_quest") {
        toast.success("Side quest started");
      } else if (triggerType === "stash") {
        toast.success("Work stashed");
      } else if (triggerType === "viewed") {
        // Silent - just tracking the view
      } else if (triggerType === "switched_focus") {
        toast.success("Switched to this focus");
      }

      setCurrentMementoId(data.id);
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());

      // Update focus tracking based on trigger type
      if (triggerType === "switched_focus") {
        // Explicitly making this the current focus
        setCurrentFocusId(displayedMementoId);
        setDisplayedMementoId(displayedMementoId);
      } else if (triggerType === "viewed") {
        // Just viewing, don't change focus
        setDisplayedMementoId(data.id);
      } else if (triggerType === "new_focus" || triggerType === "side_quest") {
        // New focus started
        setCurrentFocusId(data.id);
        setDisplayedMementoId(data.id);
      } else {
        // auto_save, manual_save, version, stash - maintain current focus
        if (!currentFocusId) {
          setCurrentFocusId(data.id);
        }
      }

      // Clear editor for New Focus and Side Quest
      if (triggerType === "new_focus" || triggerType === "side_quest") {
        analysisPanelRef.current?.setState({
          originalContent: "",
          currentContent: "",
          commandText: "",
          comments: [],
          attachments: [],
        });
        setAnalysisContent("");
        setFocusTitle("");
        setIsPinned(false);
        setIsFavorited(false);
      }

      return data;
    } catch (error: any) {
      console.error("Error saving draft:", error);
      // Only show error toasts for manual actions
      if (triggerType !== "auto_save") {
        toast.error(error.message || "Failed to save draft");
      }
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadMemento = async (memento: CurrentFocusContainerMemento) => {
    try {
      // Check if current state has unsaved changes
      const hasChanges = analysisPanelRef.current?.hasUnsavedChanges();
      if (hasChanges && hasUnsavedChanges) {
        // Auto-stash current state
        await handleSaveDraft("stash");
      }

      console.log("Loading memento:", {
        analysisAttachments: memento.analysis_attachments,
        synthesisAttachments: memento.synthesis_attachments,
      });

      // Load memento data into analysis panel
      analysisPanelRef.current?.setState({
        originalContent: memento.analysis_original_content || '',
        currentContent: memento.analysis_current_content || '',
        commandText: memento.analysis_command_text || "",
        comments: memento.analysis_comments || [],
        attachments: memento.analysis_attachments || [],
      });

      setAnalysisContent(memento.analysis_current_content || '');
      setFocusTitle(memento.custom_title || ""); // Restore the title!
      setCurrentMementoId(memento.id);
      setDisplayedMementoId(memento.id); // Track what's displayed
      setIsPinned(memento.pinned || false); // Load pin state
      setIsFavorited(memento.favorited || false); // Load favorite state
      setHasUnsavedChanges(false);

      // Create a 'history_load' event (loading a historical draft)
      await handleSaveDraft("history_load");

      toast.success(
        `Draft loaded with ${memento.analysis_attachments?.length ?? 0} attachments`
      );
    } catch (error: any) {
      console.error("Error loading memento:", error);
      toast.error(error.message || "Failed to load draft");
    }
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="current-focus-container">
      {/* Action Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background">
        {/* AI Summary Button + Editable Title */}
        <div className="flex items-center gap-2 flex-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => toast.info("Generating AI summary...")}
            title="Generate AI summary"
            className="shrink-0"
          >
            <Sparkles className="w-4 h-4" />
          </Button>
          
          {isEditingTitle ? (
            <Input
              value={focusTitle}
              onChange={(e) => {
                setFocusTitle(e.target.value.substring(0, 50));
                setHasUnsavedChanges(true); // Trigger auto-save when title changes
              }}
              onBlur={() => setIsEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIsEditingTitle(false);
                if (e.key === 'Escape') {
                  setFocusTitle("");
                  setIsEditingTitle(false);
                }
              }}
              className="h-7 text-sm font-semibold min-w-[15ch] max-w-xs flex-shrink-0"
              placeholder="Focus title (50 chars)"
              maxLength={50}
              autoFocus
            />
          ) : (
            <h2 
              className="text-sm font-semibold cursor-pointer hover:text-primary truncate min-w-[15ch] flex-shrink-0"
              onClick={() => setIsEditingTitle(true)}
              title="Click to edit title (50 chars max)"
            >
              {focusTitle || "Current Focus"}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-3">
          <LastSavedIndicator
            lastSavedAt={lastSavedAt}
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
          />
          <div className="h-4 w-px bg-border" />
          
          {/* Main Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Primary: New Focus (with dropdown) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="default">
                  <Plus className="w-4 h-4 lg:mr-1" />
                  <span className="hidden lg:inline">New Focus</span>
                  <ChevronDown className="w-3 h-3 lg:ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleSaveDraft("new_focus")}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Focus (clears current)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSaveDraft("version")}>
                  <Save className="w-4 h-4 mr-2" />
                  Version (Ctrl+S)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => toast.info("Clone coming soon...")}>
                  <Copy className="w-4 h-4 mr-2" />
                  Clone (duplicate)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Secondary: Side Quest - clears current to focus on solving this first */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleSaveDraft("side_quest")}
              title="Detour to solve this first, then return"
            >
              <Target className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Side Quest</span>
            </Button>

            {/* Tertiary: Stash - saves current but keeps it ready to restore */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleSaveDraft("stash")}
              title="Handle interruption, plan to return"
            >
              <Archive className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Stash</span>
            </Button>

            <div className="h-4 w-px bg-border" />

            {/* Make Current Focus - only show when viewing a non-focus memento */}
            {displayedMementoId && displayedMementoId !== currentFocusId && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => handleSaveDraft("manual_save")}
                  title="Make this memento your current focus"
                  className="bg-primary hover:bg-primary/90"
                >
                  <Target className="w-4 h-4 lg:mr-1" />
                  <span className="hidden lg:inline">Make Current Focus</span>
                </Button>
                <div className="h-4 w-px bg-border" />
              </>
            )}

            {/* Quick Actions */}
            <Button
              size="sm"
              variant="ghost"
              data-testid="current-focus-favorite-button"
              onClick={async () => {
                if (!currentMementoId) {
                  toast.error("No memento loaded to favorite");
                  return;
                }
                
                const newValue = !isFavorited;
                setIsFavorited(newValue); // Optimistic UI update
                
                try {
                  // Get current state to save alongside the favorite flag
                  const analysisState = analysisPanelRef.current?.getState();
                  if (!analysisState) {
                    throw new Error("Could not get analysis panel state");
                  }

                  const { error } = await supabase
                    .from("current_focus_container_mementos")
                    .update({ 
                      favorited: newValue,
                      custom_title: focusTitle || null,
                      analysis_original_content: analysisState.originalContent || "",
                      analysis_current_content: analysisState.currentContent || "",
                      analysis_command_text: analysisState.commandText || "",
                      analysis_changes: analysisState.changes as any,
                      analysis_comments: analysisState.comments as any,
                      analysis_attachments: analysisState.attachments as any,
                    })
                    .eq("id", currentMementoId);
                  
                  if (error) throw error;
                  
                  toast.success(newValue ? "Added to favorites" : "Removed from favorites");
                } catch (error) {
                  console.error('[CurrentFocusContainer] Failed to update favorite:', error);
                  setIsFavorited(!newValue); // Revert on error
                  toast.error("Failed to update favorite");
                }
              }}
              className={isFavorited ? "text-yellow-500" : ""}
              title="Favorite"
            >
              <Star className="w-4 h-4" fill={isFavorited ? "currentColor" : "none"} />
            </Button>

            <Button
              size="sm"
              variant="ghost"
              data-testid="current-focus-pin-button"
              onClick={async () => {
                if (!currentMementoId) {
                  toast.error("No memento loaded to pin");
                  return;
                }
                
                const newValue = !isPinned;
                setIsPinned(newValue); // Optimistic UI update
                
                try {
                  // Get current state to save alongside the pin flag
                  const analysisState = analysisPanelRef.current?.getState();
                  if (!analysisState) {
                    throw new Error("Could not get analysis panel state");
                  }

                  const { error } = await supabase
                    .from("current_focus_container_mementos")
                    .update({ 
                      pinned: newValue,
                      custom_title: focusTitle || null,
                      analysis_original_content: analysisState.originalContent || "",
                      analysis_current_content: analysisState.currentContent || "",
                      analysis_command_text: analysisState.commandText || "",
                      analysis_changes: analysisState.changes as any,
                      analysis_comments: analysisState.comments as any,
                      analysis_attachments: analysisState.attachments as any,
                    })
                    .eq("id", currentMementoId);
                  
                  if (error) throw error;
                  
                  toast.success(newValue ? "Pinned draft" : "Unpinned draft");
                } catch (error) {
                  console.error('[CurrentFocusContainer] Failed to update pin:', error);
                  setIsPinned(!newValue); // Revert on error
                  toast.error("Failed to update pin");
                }
              }}
              className={isPinned ? "text-blue-500" : ""}
              title="Pin"
            >
              <Pin className="w-4 h-4" fill={isPinned ? "currentColor" : "none"} />
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={onOpenHistory}
              disabled={!onOpenHistory}
              title="View history"
            >
              <Clock className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Analysis Panel (full height) */}
      <div className="flex-1 overflow-hidden">
        <AnalysisPanel
          ref={analysisPanelRef}
          content={analysisContent}
          onChange={(newContent) => {
            setAnalysisContent(newContent);
            setHasUnsavedChanges(true);
            // If viewing a memento that's not the current focus, make it the focus on first edit
            if (displayedMementoId && displayedMementoId !== currentFocusId) {
              setCurrentFocusId(displayedMementoId);
            }
          }}
          onStateChange={() => {
            setHasUnsavedChanges(true);
            // If viewing a memento that's not the current focus, make it the focus on first edit
            if (displayedMementoId && displayedMementoId !== currentFocusId) {
              setCurrentFocusId(displayedMementoId);
            }
            
            // Check if AI just modified content - if so, trigger iterate save
            if (analysisPanelRef.current?.wasModifiedByAI()) {
              analysisPanelRef.current.clearAIModifiedFlag();
              // Trigger iterate save after a short delay to let state settle
              setTimeout(() => {
                handleSaveDraft("iterate");
              }, 100);
            }
          }}
          onCancel={() => {
            setHasUnsavedChanges(false);
          }}
          onSave={() => handleSaveDraft("manual_save")}
        />
      </div>
    </div>
  );
});
